// Stripe Payouts — finance reconciliation of Stripe BANK DEPOSITS for ICCT + Kotilinga. 100% Stripe-sourced
// (Payouts + Balance Transactions); no LDB/order joins. Mirrors the approved mock: summary tiles, by-account,
// by-method (Card/ACH donut + card brands), the payouts ledger, and a row-click full payout dialog (all its
// charges/refunds + the manual bank-match flag + comment). Reads /api/manage/stripe-payouts*.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Button, Chip, Table, TableBody, TableCell, TableHead, TableRow, TableFooter, CircularProgress,
  Alert, Snackbar, TextField, Select, MenuItem, FormControl, Dialog, DialogContent, IconButton, Switch, Tooltip,
  InputAdornment, ToggleButton, ToggleButtonGroup, Link,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { alpha } from '@mui/material/styles';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer } from 'recharts';
import PageLayout from '../../components/common/PageLayout';
import { get, post } from '../../utils/api';

const ACCT_LABEL = { icct: 'ICCT', kotilinga: 'Kotilinga' };
// Deep links into the Stripe dashboard (account-prefixed so they resolve for either Stripe account).
const STRIPE_ACCT = { icct: 'acct_1E1OIZKPu9FqJB4c', kotilinga: 'acct_1FvSFiLlEaQtq9Uo' };
const dashBase = (acct) => `https://dashboard.stripe.com/${STRIPE_ACCT[acct] ? `${STRIPE_ACCT[acct]}/` : ''}`;
const payoutLink = (acct, id) => `${dashBase(acct)}payouts/${id}`;
const chargeLink = (acct, id) => ((id && (id.startsWith('ch_') || id.startsWith('pi_') || id.startsWith('py_'))) ? `${dashBase(acct)}payments/${id}` : null);
// LDB context for a tx (read-only) — shown on hover of the info icon. Payout figures stay 100% Stripe.
function ldbTooltip(t) {
  const l = t.ldb;
  return (
    <Box sx={{ py: 0.5, lineHeight: 1.6, fontSize: 12.5 }}>
      {l && l.order_number ? <div>Order <b>#{l.order_number}</b>{l.order_status ? ` · ${l.order_status}` : ''}</div> : null}
      {l && l.donor ? <div>Donor: {l.donor}</div> : null}
      {l && l.email ? <div style={{ opacity: 0.85 }}>{l.email}</div> : null}
      {l && l.products ? <div>For: {l.products}</div> : null}
      {l && l.description ? <div style={{ opacity: 0.8 }}>{l.description}</div> : null}
      {l && l.origin ? <div style={{ opacity: 0.8 }}>Origin: {l.origin}</div> : null}
      {t.refund_reason ? <div>Refund reason: {t.refund_reason}</div> : null}
      {!l && !t.refund_reason ? <div>No linked order found in LDB</div> : null}
    </Box>
  );
}
const fmtMoney = (n) => (n == null ? '—' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtDate = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const fmtDT = (d) => (d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');
const fmtTxnDate = (d) => (d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');
const TXN_TYPE = { payment: ['Payment', 'success'], refund: ['Refund', 'error'], chargeback: ['Chargeback', 'warning'], adjustment: ['Adjustment', 'default'], other: ['Adjustment', 'default'] };
const txnType = (k) => TXN_TYPE[k] || ['Adjustment', 'default'];
const statusColor = (s) => (s === 'paid' ? 'success' : s === 'in_transit' ? 'warning' : 'default');
const statusLabel = (s) => ({ paid: 'Paid', in_transit: 'In transit', pending: 'Pending' }[s] || s || '—');
const pct = (a, b) => (b > 0 ? `${((a / b) * 100).toFixed(1)}%` : '—');
const effRate = (fee, gross) => (gross > 0 ? `${((fee / gross) * 100).toFixed(2)}%` : '—');

// Two time controls, both driving the same `period` state:
//  • PERIOD = typical relative presets (This month / Last month / This quarter / This year / Last year /
//    All time / Custom range) — all COMPUTED from today, nothing hardcoded.
//  • YEAR   = jump to a specific full year (2019→now). When set, period becomes `y:<year>`.
const NOW_Y = new Date().getFullYear();
const YEAR_OPTS = Array.from({ length: NOW_Y - 2019 + 1 }, (_, i) => String(NOW_Y - i)); // newest first
const dstr = (dt) => dt.toISOString().slice(0, 10);
function buildPeriods() {
  const now = new Date(); const y = now.getFullYear(); const m = now.getMonth();
  const mRange = (yy, mm) => ({ from: dstr(new Date(yy, mm, 1)), to: dstr(new Date(yy, mm + 1, 0)) });
  const lm = new Date(y, m - 1, 1);           // last month (handles Jan → Dec prev year)
  const qs = Math.floor(m / 3) * 3;           // first month of the current quarter
  return [
    { value: 'this_month', label: 'This month', short: 'this month', ...mRange(y, m) },
    { value: 'last_month', label: 'Last month', short: 'last month', ...mRange(lm.getFullYear(), lm.getMonth()) },
    { value: 'this_quarter', label: 'This quarter', short: 'this quarter', from: dstr(new Date(y, qs, 1)), to: dstr(new Date(y, qs + 3, 0)) },
    { value: 'this_year', label: 'This year', short: String(y), from: dstr(new Date(y, 0, 1)), to: dstr(new Date(y, 11, 31)) },
    { value: 'last_year', label: 'Last year', short: String(y - 1), from: dstr(new Date(y - 1, 0, 1)), to: dstr(new Date(y - 1, 11, 31)) },
    { value: 'all', label: 'All time', short: 'all time', from: null, to: null },
    { value: 'custom', label: 'Custom range…', short: 'custom' },
  ];
}
const PERIODS = buildPeriods();

function Tile({ label, value, sub, color }) {
  return (
    <Paper elevation={0} sx={(t) => ({
      p: 1.7, borderRadius: 2.5, minWidth: 0,
      border: `1px solid ${alpha(t.palette[color]?.main || t.palette.primary.main, 0.28)}`,
      borderTop: `3px solid ${t.palette[color]?.main || t.palette.primary.main}`,
      background: `linear-gradient(180deg, ${alpha(t.palette[color]?.main || t.palette.primary.main, 0.07)} 0%, ${alpha(t.palette[color]?.main || t.palette.primary.main, 0.01)} 100%)`,
      boxShadow: '0 1px 2px rgba(16,24,40,.05), 0 4px 12px rgba(16,24,40,.04)',
    })}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.4, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
      {sub && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{sub}</Typography>}
    </Paper>
  );
}

// ---- Full payout dialog: payout detail + all its transactions + bank-match + comment ----
function PayoutDialog({ open, payoutId, onClose, onChanged, setToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [txFilter, setTxFilter] = useState('all');
  const [q, setQ] = useState('');
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');

  const load = useCallback(async () => {
    if (!open || !payoutId) return;
    setLoading(true); setErr(''); setTxFilter('all'); setQ('');
    try { setData(await get(`/api/manage/stripe-payouts/${encodeURIComponent(payoutId)}`)); }
    catch (e) { setErr(e?.response?.data?.error || e?.message || 'Failed to load payout'); }
    finally { setLoading(false); }
  }, [open, payoutId]);
  useEffect(() => { load(); }, [load]);

  const p = data?.payout;
  const txns = useMemo(() => {
    const list = data?.txns || [];
    const s = q.trim().toLowerCase();
    return list.filter((t) => (txFilter === 'all' || (txFilter === 'pay' ? t.kind === 'payment' : t.kind === 'refund'))
      && (!s || `${t.name || ''} ${t.src_id || ''} ${t.bt_id || ''}`.toLowerCase().includes(s)));
  }, [data, txFilter, q]);

  const saveMatch = async (matched) => {
    try {
      await post(`/api/manage/stripe-payouts/${encodeURIComponent(p.payout_id)}/bank-match`, { account: p.account, bank_status: matched ? 'matched' : 'unmatched', note: p.note || undefined });
      setToast(matched ? 'Marked matched' : 'Marked unmatched'); await load(); onChanged && onChanged();
    } catch (e) { setToast(e?.response?.data?.error || 'Save failed'); }
  };
  const saveNote = async () => {
    try {
      await post(`/api/manage/stripe-payouts/${encodeURIComponent(p.payout_id)}/bank-match`, { account: p.account, bank_status: p.bank_status, note: noteDraft.trim() || undefined });
      setNoteOpen(false); setToast('Comment saved'); await load(); onChanged && onChanged();
    } catch (e) { setToast(e?.response?.data?.error || 'Save failed'); }
  };
  const exportTxns = () => {
    if (!p) return;
    const cols = ['created_at', 'kind', 'method', 'card_brand', 'name', 'src_id', 'gross_num', 'fee_num', 'net_num'];
    const esc = (v) => { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const csv = [cols.join(',')].concat((data?.txns || []).map((r) => cols.map((c) => esc(r[c])).join(','))).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = `payout-${p.payout_id}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogContent sx={{ p: 0 }}>
        {loading ? <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress size={24} /></Box>
          : err ? <Alert severity="error" sx={{ m: 2 }}>{err}</Alert>
            : p ? (
              <>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Chip size="small" label={ACCT_LABEL[p.account] || p.account} color="primary" variant="outlined" />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>Payout · {fmtDate(p.arrival_date)}</Typography>
                      <Chip size="small" color={statusColor(p.status)} variant="outlined" label={statusLabel(p.status)} />
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}><Link href={payoutLink(p.account, p.payout_id)} target="_blank" rel="noopener" underline="hover">{p.payout_id}</Link>{p.bank_last4 ? ` · to bank ****${p.bank_last4}` : ''}{p.method ? ` · ${p.method}` : ''}</Typography>
                  </Box>
                  <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
                </Box>

                {(() => {
                  const adj = Number(p.adjustments_num || 0);
                  const boxes = [
                    ['Gross', fmtMoney(p.gross_num), 'text.primary'],
                    ['Refunds', p.refunds_num ? `-${fmtMoney(p.refunds_num)}` : '—', p.refunds_num ? 'error.main' : 'text.primary'],
                    ['Fees', `-${fmtMoney(p.fees_num)}`, 'error.main'],
                    ...(Math.abs(adj) >= 0.005 ? [['Adjustments', `${adj < 0 ? '-' : ''}${fmtMoney(Math.abs(adj))}`, 'error.main']] : []),
                    ['Net deposited', fmtMoney(p.net_num), 'success.main'],
                    ['Transactions', p.txn_count, 'text.primary'],
                  ];
                  return (
                    <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${boxes.length}, 1fr)`, borderBottom: 1, borderColor: 'divider' }}>
                      {boxes.map(([k, v, color], i) => (
                        <Box key={k} sx={{ p: 1.3, borderLeft: i ? 1 : 0, borderColor: 'divider' }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>{k}</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color }}>{v}</Typography>
                        </Box>
                      ))}
                    </Box>
                  );
                })()}

                <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Switch checked={p.bank_status === 'matched'} onChange={(e) => saveMatch(e.target.checked)} color="success" />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: p.bank_status === 'matched' ? 'success.main' : 'text.secondary' }}>{p.bank_status === 'matched' ? 'Matched at bank' : 'Unmatched'}</Typography>
                  </Box>
                  <Button size="small" startIcon={<ChatBubbleOutlineIcon />} onClick={() => { setNoteDraft(p.note || ''); setNoteOpen(true); }}>{p.note ? 'Edit comment' : 'Add comment'}</Button>
                  {p.note && <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>“{p.note}”</Typography>}
                </Box>

                <Box sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  {[['all', 'All'], ['pay', 'Payments'], ['ref', 'Refunds']].map(([v, l]) => (
                    <Chip key={v} size="small" label={l} color={txFilter === v ? 'primary' : 'default'} variant={txFilter === v ? 'filled' : 'outlined'} onClick={() => setTxFilter(v)} clickable />
                  ))}
                  <Box sx={{ flex: 1 }} />
                  <TextField size="small" placeholder="Search name / charge id" value={q} onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 200 }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
                  <Button size="small" startIcon={<FileDownloadOutlinedIcon />} onClick={exportTxns}>Export this payout</Button>
                </Box>

                <Box sx={{ maxHeight: '44vh', overflow: 'auto', borderTop: 1, borderColor: 'divider' }}>
                  <Table size="small" stickyHeader>
                    <TableHead><TableRow sx={{ '& th': { bgcolor: 'grey.100', fontWeight: 700 } }}>
                      <TableCell>Date</TableCell><TableCell>Type</TableCell><TableCell>Method</TableCell><TableCell>Name (billing)</TableCell><TableCell>Charge</TableCell>
                      <TableCell align="right">Gross</TableCell><TableCell align="right">Fee</TableCell><TableCell align="right">Net</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {txns.length === 0 && <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', color: 'text.secondary', py: 2 }}>No transactions match.</TableCell></TableRow>}
                      {txns.map((t, i) => (
                        <TableRow key={t.bt_id || i} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtTxnDate(t.created_at)}</TableCell>
                          <TableCell><Chip size="small" variant="outlined" color={txnType(t.kind)[1]} label={txnType(t.kind)[0]} /></TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{t.method || '—'}{t.card_brand ? ` · ${t.card_brand}` : ''}</TableCell>
                          <TableCell>{t.name || '—'}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                            {chargeLink(p.account, t.src_id) ? <Link href={chargeLink(p.account, t.src_id)} target="_blank" rel="noopener" underline="hover">{t.src_id}</Link> : (t.src_id || t.bt_id)}
                            {(t.ldb || t.refund_reason) && <Tooltip arrow title={ldbTooltip(t)}><InfoOutlinedIcon sx={{ fontSize: 15, ml: 0.5, color: 'primary.main', cursor: 'help', verticalAlign: 'text-bottom' }} /></Tooltip>}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: t.gross_num < 0 ? 'error.main' : 'inherit' }}>{fmtMoney(t.gross_num)}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: t.fee_num ? 'error.main' : 'inherit' }}>{t.fee_num ? `-${fmtMoney(t.fee_num)}` : '—'}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(t.net_num)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell colSpan={7} align="right" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.85rem', borderTop: 2, borderColor: 'divider' }}>Net deposited</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main', fontSize: '0.85rem', borderTop: 2, borderColor: 'divider', fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(p.net_num)}</TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </Box>
              </>
            ) : null}
      </DialogContent>

      <Dialog open={noteOpen} onClose={() => setNoteOpen(false)} maxWidth="sm" fullWidth>
        <DialogContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Payout comment</Typography>
          <TextField multiline minRows={4} fullWidth value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} placeholder="Bank statement ref, discrepancy, who confirmed…" />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button onClick={() => setNoteOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={saveNote}>Save comment</Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

export default function StripePayoutsPage() {
  const [account, setAccount] = useState('all');
  const [period, setPeriod] = useState(PERIODS[0].value);   // default = current month (restored duration dropdown)
  const [cFrom, setCFrom] = useState('');
  const [cTo, setCTo] = useState('');
  const [bank, setBank] = useState('all');
  const [status, setStatus] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [err, setErr] = useState('');
  const [toast, setToast] = useState('');
  const [detailId, setDetailId] = useState(null);

  // period is a preset key, 'custom', or 'y:<year>' (a specific year chosen in the YEAR dropdown).
  const yearSel = period.startsWith('y:') ? period.slice(2) : '';
  const presetOpt = PERIODS.find((p) => p.value === period) || null;
  let range; let periodLabel; let periodShort;
  if (period === 'custom') { range = { from: cFrom || null, to: cTo || null }; periodLabel = `${cFrom || '…'} – ${cTo || '…'}`; periodShort = `${cFrom || '…'}–${cTo || '…'}`; }
  else if (yearSel) { range = { from: `${yearSel}-01-01`, to: `${yearSel}-12-31` }; periodLabel = yearSel; periodShort = yearSel; }
  else { const o = presetOpt || PERIODS[0]; range = { from: o.from, to: o.to }; periodLabel = o.label; periodShort = o.short; }
  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try { setData(await get('/api/manage/stripe-payouts', { account, from: range.from || undefined, to: range.to || undefined, bank, status })); }
    catch (e) { setErr(e?.response?.data?.error || e?.message || 'Failed to load payouts'); }
    finally { setLoading(false); }
  }, [account, range.from, range.to, bank, status]);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try { await post('/api/manage/stripe-payouts/sync', {}); setToast('Synced from Stripe'); await load(); }
    catch (e) { setToast(e?.response?.data?.error || 'Sync failed'); }
    finally { setSyncing(false); }
  };
  const exportUrl = `/api/manage/stripe-payouts/export?account=${account}&from=${range.from || ''}&to=${range.to || ''}&bank=${bank}&status=${status}`;
  const setMatch = async (p, matched) => {
    try { await post(`/api/manage/stripe-payouts/${encodeURIComponent(p.payout_id)}/bank-match`, { account: p.account, bank_status: matched ? 'matched' : 'unmatched', note: p.note || undefined }); await load(); }
    catch (e) { setToast(e?.response?.data?.error || 'Save failed'); }
  };

  const s = data?.summary || {};
  const bm = data?.by_method || {};
  const payouts = data?.payouts || [];
  const grossTot = Number(bm.gross_card || 0) + Number(bm.gross_ach || 0);
  const cardPctNum = grossTot > 0 ? (Number(bm.gross_card || 0) / grossTot) * 100 : 0;
  // Net-deposited bar chart for the selected period (adaptive granularity: year / month / per-payout day).
  const chartData = useMemo(() => {
    const ps = (data?.payouts || []).filter((p) => p.arrival_date);
    if (!ps.length) return { rows: [], unit: '' };
    const sorted = ps.map((p) => p.arrival_date).sort();
    const spanDays = (new Date(sorted[sorted.length - 1]) - new Date(sorted[0])) / 86400000;
    const unit = spanDays > 550 ? 'year' : spanDays > 62 ? 'month' : 'day';
    const map = {};
    ps.forEach((p) => {
      const d = new Date(`${String(p.arrival_date).slice(0, 10)}T12:00:00`);
      let key; let label;
      if (unit === 'year') { key = `${d.getFullYear()}`; label = key; }
      else if (unit === 'month') { key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; label = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }); }
      else { key = p.arrival_date; label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
      if (!map[key]) map[key] = { key, label, net: 0 };
      map[key].net += Number(p.net_num || 0);
    });
    return { rows: Object.values(map).sort((a, b) => (a.key < b.key ? -1 : 1)), unit };
  }, [data]);

  return (
    <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Stripe Payouts</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="outlined" startIcon={syncing ? <CircularProgress size={14} /> : <RefreshIcon />} disabled={syncing} onClick={sync}>Sync</Button>
            <Button size="small" variant="contained" startIcon={<FileDownloadOutlinedIcon />} component="a" href={exportUrl}>Export CSV</Button>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>Last refreshed: {data?.last_synced ? fmtDT(data.last_synced) : '—'}</Typography>
        </Box>
      </Box>

      {/* controls — segmented ACCOUNT + month PERIOD + Bank + Status (matches the mock) */}
      <Paper elevation={0} sx={{ p: 1.3, mb: 2, display: 'flex', gap: 1.2, alignItems: 'center', flexWrap: 'wrap', borderRadius: 2.5, border: '1px solid', borderColor: 'divider', bgcolor: alpha('#2e3c6e', 0.03), boxShadow: '0 1px 2px rgba(16,24,40,.04)' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}>ACCOUNT</Typography>
        <ToggleButtonGroup size="small" exclusive value={account} onChange={(e, v) => v && setAccount(v)} color="primary">
          <ToggleButton value="all" sx={{ textTransform: 'none', px: 1.8 }}>All</ToggleButton>
          <ToggleButton value="icct" sx={{ textTransform: 'none', px: 1.8 }}>ICCT</ToggleButton>
          <ToggleButton value="kotilinga" sx={{ textTransform: 'none', px: 1.8 }}>Kotilinga</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5, ml: 1 }}>PERIOD</Typography>
        <FormControl size="small"><Select value={presetOpt ? period : ''} displayEmpty
          renderValue={() => (presetOpt ? presetOpt.label : yearSel ? `Year: ${yearSel}` : 'Period…')}
          onChange={(e) => setPeriod(e.target.value)} sx={{ minWidth: 160 }}>
          {PERIODS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
        </Select></FormControl>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5, ml: 1 }}>YEAR</Typography>
        <FormControl size="small"><Select value={yearSel} displayEmpty
          renderValue={() => (yearSel || 'Year…')}
          onChange={(e) => setPeriod(`y:${e.target.value}`)} sx={{ minWidth: 110 }}>
          {YEAR_OPTS.map((yy) => <MenuItem key={yy} value={yy}>{yy}</MenuItem>)}
        </Select></FormControl>
        {period === 'custom' && (
          <>
            <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={cFrom} onChange={(e) => setCFrom(e.target.value)} sx={{ width: 150 }} />
            <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={cTo} onChange={(e) => setCTo(e.target.value)} sx={{ width: 150 }} />
          </>
        )}
        <Box sx={{ flex: 1 }} />
        <FormControl size="small"><Select value={bank} onChange={(e) => setBank(e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="all">Bank: all</MenuItem><MenuItem value="unmatched">Unmatched</MenuItem><MenuItem value="matched">Matched</MenuItem>
        </Select></FormControl>
        <FormControl size="small"><Select value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="all">Status: all</MenuItem><MenuItem value="paid">Paid</MenuItem><MenuItem value="in_transit">In transit</MenuItem><MenuItem value="pending">Pending</MenuItem>
        </Select></FormControl>
      </Paper>

      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}

      {/* tiles */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)', lg: 'repeat(5,1fr)' }, gap: 1.5, mb: 1.5 }}>
        <Tile label="Net deposited to bank" value={fmtMoney(s.net)} sub={`${s.payouts || 0} payouts · ${periodShort}`} color="success" />
        <Tile label="Gross payments" value={fmtMoney(s.gross)} sub={`${s.charges || 0} charges`} color="primary" />
        <Tile label="Stripe fees" value={fmtMoney(s.fees)} sub={`${effRate(Number(s.fees || 0), Number(s.gross || 0))} effective`} color="warning" />
        <Tile label="Refunds" value={fmtMoney(s.refunds)} color="error" />
        <Tile label="Pending balance" value={fmtMoney(s.pending)} sub="not yet paid out" color="warning" />
      </Box>

      {/* by account + by method */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' }, gap: 1.5, mb: 2 }}>
        <Paper elevation={0} sx={{ p: 1.8, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', boxShadow: '0 1px 2px rgba(16,24,40,.04), 0 6px 18px rgba(16,24,40,.05)' }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700 }}>By account · {periodLabel}</Typography>
          {(data?.by_account || []).map((a) => (
            <Box key={a.account} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.8, borderBottom: 1, borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}>
              <Typography variant="body2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}><Box component="span" sx={{ width: 9, height: 9, borderRadius: '2px', mr: 1, bgcolor: a.account === 'icct' ? '#c8781e' : '#2e3c6e' }} />{ACCT_LABEL[a.account] || a.account}{a.bank_last4 ? <Box component="span" sx={{ color: 'text.secondary', fontFamily: 'monospace', ml: 1 }}>****{a.bank_last4}</Box> : null}</Typography>
              <Box sx={{ display: 'flex', gap: 2.5, textAlign: 'right' }}>
                {[['gross', a.gross], ['net', a.net], ['fees', a.fees], ['payouts', a.payouts]].map(([k, v]) => (
                  <Box key={k}><Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{k === 'payouts' ? v : fmtMoney(v)}</Typography><Typography variant="caption" sx={{ color: 'text.secondary' }}>{k}</Typography></Box>
                ))}
              </Box>
            </Box>
          ))}
          {(!data?.by_account || data.by_account.length === 0) && <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>No payouts in this period.</Typography>}
          {chartData.rows.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase' }}>Net deposited {chartData.unit === 'year' ? 'by year' : chartData.unit === 'month' ? 'by month' : 'by payout'}</Typography>
              <Box sx={{ height: 200, mt: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData.rows} margin={{ top: 5, right: 6, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e3e7ee" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" minTickGap={10} />
                    <YAxis tick={{ fontSize: 10 }} width={44} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                    <RTooltip formatter={(v) => [fmtMoney(v), 'Net deposited']} contentStyle={{ fontSize: 12 }} />
                    <Bar dataKey="net" fill="#2e3c6e" radius={[3, 3, 0, 0]} maxBarSize={48} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          )}
        </Paper>

        <Paper elevation={0} sx={{ p: 1.8, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', boxShadow: '0 1px 2px rgba(16,24,40,.04), 0 6px 18px rgba(16,24,40,.05)' }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700 }}>By payment method · gross &amp; actual Stripe fees</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
            <Box sx={{ width: 96, height: 96, borderRadius: '50%', flex: 'none',
              background: `conic-gradient(#c8781e 0 ${cardPctNum}%, #2e3c6e ${cardPctNum}% 100%)`,
              WebkitMask: 'radial-gradient(circle 30px at center, transparent 98%, #000 100%)', mask: 'radial-gradient(circle 30px at center, transparent 98%, #000 100%)' }} />
            <Box sx={{ flex: 1, minWidth: 150 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}><Box component="span" sx={{ width: 10, height: 10, bgcolor: '#c8781e', borderRadius: '2px', display: 'inline-block', mr: 1 }} />Card</Typography>
                <Typography variant="body2" sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(bm.gross_card)}<Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>{pct(Number(bm.gross_card || 0), grossTot)} · fee {fmtMoney(bm.fees_card)} · eff {effRate(Number(bm.fees_card || 0), Number(bm.gross_card || 0))} · {bm.count_card || 0} chg</Typography></Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}><Box component="span" sx={{ width: 10, height: 10, bgcolor: '#2e3c6e', borderRadius: '2px', display: 'inline-block', mr: 1 }} />ACH / bank</Typography>
                <Typography variant="body2" sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(bm.gross_ach)}<Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>{pct(Number(bm.gross_ach || 0), grossTot)} · fee {fmtMoney(bm.fees_ach)} · eff {effRate(Number(bm.fees_ach || 0), Number(bm.gross_ach || 0))} · {bm.count_ach || 0} chg</Typography></Typography>
              </Box>
            </Box>
          </Box>
          {((data?.brands || []).length > 0 || Number(bm.gross_ach || 0) > 0) && (
            <Box sx={{ mt: 1.5, pt: 1, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase' }}>ACH &amp; Card brands — gross &amp; actual fees (from Stripe)</Typography>
              {[
                ...(Number(bm.gross_ach || 0) > 0 ? [{ label: 'ACH / bank', gross: Number(bm.gross_ach || 0), fees: Number(bm.fees_ach || 0), ach: true }] : []),
                ...(data?.brands || []).map((b) => ({ label: b.card_brand, gross: Number(b.gross || 0), fees: Number(b.fees || 0) })),
              ].map((b) => (
                <Box key={b.label} sx={{ display: 'grid', gridTemplateColumns: '90px 1fr auto', gap: 1, alignItems: 'center', py: 0.4 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{b.label}</Typography>
                  <Box sx={{ height: 7, borderRadius: 1, bgcolor: 'grey.200', overflow: 'hidden' }}><Box sx={{ height: '100%', bgcolor: b.ach ? '#2e3c6e' : 'primary.main', width: pct(b.gross, grossTot || 1) }} /></Box>
                  <Typography variant="caption" sx={{ textAlign: 'right', fontFamily: 'monospace' }}>{fmtMoney(b.gross)}<Box component="span" sx={{ display: 'block', color: 'text.secondary' }}>fee {fmtMoney(b.fees)} · {effRate(b.fees, b.gross)}</Box></Typography>
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      </Box>

      {/* payouts ledger */}
      <Paper elevation={0} sx={{ borderRadius: 2.5, overflow: 'hidden', border: '1px solid', borderColor: 'divider', boxShadow: '0 1px 2px rgba(16,24,40,.04), 0 6px 18px rgba(16,24,40,.05)' }}>
        <Box sx={{ px: 2, py: 1.2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Payouts <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>— one bank deposit per row · click to open its full detail</Box></Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>{payouts.length} payouts</Typography>
        </Box>
        {loading ? <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress size={24} /></Box>
          : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 820 }}>
                <TableHead><TableRow sx={{ '& th': { bgcolor: 'grey.50', fontWeight: 700, whiteSpace: 'nowrap' } }}>
                  <TableCell>Arrival date</TableCell><TableCell>Account</TableCell><TableCell>Payout ID</TableCell><TableCell>To bank</TableCell><TableCell>Status</TableCell>
                  <TableCell align="right">Gross</TableCell><TableCell align="right">Refunds</TableCell><TableCell align="right">Fees</TableCell><TableCell align="right">Net deposited</TableCell><TableCell align="right">Txns</TableCell><TableCell>Bank match</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {payouts.length === 0 && <TableRow><TableCell colSpan={11} sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>No payouts. Click Sync to pull from Stripe.</TableCell></TableRow>}
                  {payouts.map((p) => (
                    <TableRow key={p.payout_id} hover sx={{ cursor: 'pointer' }} onClick={() => setDetailId(p.payout_id)}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(p.arrival_date)}</TableCell>
                      <TableCell><Chip size="small" variant="outlined" color={p.account === 'icct' ? 'warning' : 'secondary'} label={ACCT_LABEL[p.account] || p.account} /></TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }} onClick={(e) => e.stopPropagation()}>
                        <Link href={payoutLink(p.account, p.payout_id)} target="_blank" rel="noopener" underline="hover">{p.payout_id}</Link>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{p.bank_last4 ? `****${p.bank_last4}` : '—'}</TableCell>
                      <TableCell><Chip size="small" variant="outlined" color={statusColor(p.status)} label={statusLabel(p.status)} /></TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(p.gross_num)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: p.refunds_num > 0 ? 'error.main' : 'text.secondary' }}>{p.refunds_num > 0 ? `-${fmtMoney(p.refunds_num)}` : '—'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'error.main' }}>-{fmtMoney(p.fees_num)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: 'success.main' }}>{fmtMoney(p.net_num)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{p.txn_count}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                          <Switch size="small" checked={p.bank_status === 'matched'} onChange={(e) => setMatch(p, e.target.checked)} color="success" />
                          <Typography variant="caption" sx={{ fontWeight: 600, color: p.bank_status === 'matched' ? 'success.main' : 'text.secondary' }}>{p.bank_status === 'matched' ? 'Matched' : 'Unmatched'}</Typography>
                          {p.note && <Tooltip title={p.note}><ChatBubbleOutlineIcon fontSize="small" sx={{ ml: 0.5, color: 'primary.main' }} /></Tooltip>}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
      </Paper>

      <PayoutDialog open={!!detailId} payoutId={detailId} onClose={() => setDetailId(null)} onChanged={load} setToast={setToast} />
      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </PageLayout>
  );
}
